import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { NoPatient } from '../../assets/images';
import { appPages } from '../../config/pages.config';
import { useApiReload } from '../../context/apiRelaod';
import { useAudioRecording } from '../../context/AudioRecordingContext';
import { useAuth } from '../../context/authContext';
import EditPatientPage from '../../pages/EditPatient.page';
import { DeletePatient, PatientList } from '../../services/apiService';
import { removeDataSingle } from '../../utils/indexedDB';
import { AsideBody } from '../layouts/Aside/Aside';
import Button from './Button';
import Modal, { ModalBody, ModalFooter, ModalHeader } from './Modal';
import SimpleLoader from './SimpleLoader';

type PatientListProps = {
	current: any;
	filteredPatients: any[];
	selectedPatientId: string | null;
	onSelect: (patient: any) => void;
	onEdit: (patient: any) => void;
	onDelete: (patient: any) => void;
};

const PatientListCompo = React.memo(
	({
		current,
		filteredPatients,
		selectedPatientId,
		onSelect,
		onEdit,
		onDelete,
	}: PatientListProps) => {

		return (
			<div
				className='overflow-y-auto max-h-[calc(100vh-280px)] scrollbar-thin scrollbar-track-transparent [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded'
				style={{
					scrollbarColor: '#08203E transparent',
				}}
				onMouseEnter={(e) => {
					e.currentTarget.style.scrollbarColor = '#08203E transparent';
				}}
				onMouseLeave={(e) => {
					e.currentTarget.style.scrollbarColor = '#08203E transparent';
				}}>
				<ul className='patient-list space-y-1.5'>
					{filteredPatients.length > 0 &&
						filteredPatients.map((patient: any) => (
							<li
								key={patient.id}
								className={`patient-card rounded-lg border transition-all duration-150 cursor-pointer overflow-hidden ${
									selectedPatientId === patient.id
										? 'secondary-bg-opacity border-l-4 primary-border-left border-t-blue-100 border-r-blue-100 border-b-blue-100'
										: 'bg-white border-gray-200 hover:border-l-4 hover:border-l-[#08203e] hover:border-t-gray-200 hover:border-r-gray-200 hover:border-b-gray-200'
								}`}
								onClick={() => {
									onSelect(patient);
								}}>
								<div className='p-2.5'>
									<div className='flex items-center justify-between'>
										<div className='flex flex-col pr-2'>
											<p
												className='w-40 truncate text-base font-semibold text-gray-800'
												title={patient.firstName + ' ' + patient.lastName}>
												{patient.firstName + '  ' + patient.lastName}
											</p>
											<p
												className='w-40 truncate text-xs text-gray-500'
												title={patient.email}>
												{patient.email}
											</p>
										</div>
										<div className='flex gap-1'>
											<button
												className='flex items-center justify-center h-7 w-7 rounded-md hover:primary-bg transition-colors'
												onClick={(e) => {
													e.stopPropagation();
													onEdit(patient);
												}}>
												<svg
													xmlns='http://www.w3.org/2000/svg'
													x='0px'
													y='0px'
													width='14'
													height='14'
													viewBox='0 0 24 24'
													className='text-blue-600'>
													<path d='M 18 2 L 15.585938 4.4140625 L 19.585938 8.4140625 L 22 6 L 18 2 z M 14.076172 5.9238281 L 3 17 L 3 21 L 7 21 L 18.076172 9.9238281 L 14.076172 5.9238281 z'></path>
												</svg>
											</button>
											<button
												className='flex items-center justify-center h-7 w-7 rounded-md hover:bg-red-50 transition-colors'
												onClick={(e) => {
													e.stopPropagation();
													onDelete(patient);
												}}>
												<svg
													xmlns='http://www.w3.org/2000/svg'
													width='14px'
													height='14px'
													viewBox='0 0 24 24'
													fill='none'
													className='text-red-500'>
													<path
														d='M10 12V17'
														stroke='currentColor'
														strokeWidth='2'
														strokeLinecap='round'
														strokeLinejoin='round'
													/>
													<path
														d='M14 12V17'
														stroke='currentColor'
														strokeWidth='2'
														strokeLinecap='round'
														strokeLinejoin='round'
													/>
													<path
														d='M4 7H20'
														stroke='currentColor'
														strokeWidth='2'
														strokeLinecap='round'
														strokeLinejoin='round'
													/>
													<path
														d='M6 10V18C6 19.6569 7.34315 21 9 21H15C16.6569 21 18 19.6569 18 18V10'
														stroke='currentColor'
														strokeWidth='2'
														strokeLinecap='round'
														strokeLinejoin='round'
													/>
													<path
														d='M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5V7H9V5Z'
														stroke='currentColor'
														strokeWidth='2'
														strokeLinecap='round'
														strokeLinejoin='round'
													/>
												</svg>
											</button>
										</div>
									</div>
								</div>
							</li>
						))}
				</ul>
			</div>
		);
	},
);

const PatientDetails = () => {
	const { onLogout } = useAuth();
	const { setReloadPatient, isRecording, isOnNewNoteScreen } = useApiReload();

	// Use the dictionary-based recording context
	const { recordings, pauseRecording, deleteRecording } = useAudioRecording();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [edited, setedited] = useState(false);
	const [notSavedData, setNotSavedData] = useState(false);
	const [PId, setPId] = useState();
	const [filteredPatients, setFilteredPatients] = useState<any>([]);
	const [asideBodyClass, setAsideBodyClass] = useState('');
	const [patientToEdit, setPatientToEdit] = useState<any>(null);

	const handleOpenModal = () => setIsModalOpen(true);
	const handleCloseModal = () => setIsModalOpen(false);
	const { patientID, setPatientID } = useApiReload();

	const [paginationData, setPagination] = useState<{
		current: number;
		totalPages: number;
		loadMorePage?: boolean;
	}>({ current: 1, totalPages: 0 });

	const navigate = useNavigate();
	const { response, loading, call } = PatientList();
	useEffect(() => {
		call(paginationData.current).catch((e) => {
			console.log(e);
		});
	}, []);

	const { response_del, call_del } = DeletePatient();

	const [searchTerm, setSearchTerm] = useState('');
	const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
		patientID?.id || null,
	);


	useEffect(() => {
		let isMounted = true;

		const fetchPatients = debounce(() => {
			call(null, searchTerm)
				.then((res) => {
					const patientList = res?.patientList ? Object.values(res.patientList) : [];

					const filteredPatients1 = patientList.filter((patient: any) => {
						const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
						return fullName.includes(searchTerm.toLowerCase());
					});

					if (isMounted) {
						setFilteredPatients(filteredPatients1);
					}
				})
				.catch((e) => {
					if (e?.status === 409) {
						onLogout(e?.status);
					}
					toast.error(e?.data?.msg || e.message);
				});
		}, 1000);

		fetchPatients();

		return () => {
			isMounted = false;
			fetchPatients.cancel();
		};
	}, [searchTerm]);


	const loadmore = () => {
		let current = paginationData.current;
		let totalPages = paginationData.totalPages;
		let loadMorePage = false;
		if (current < totalPages) {
			loadMorePage = true;
			current++;
			call(current).catch((e) => {
				if (e?.status === 409) {
					onLogout(e?.status);
				}
				toast.error(e?.data?.msg || e.message);
			});
		}

		setPagination({ current: current, totalPages: totalPages, loadMorePage });
	};

	const handleredirect = useCallback((patient: any) => {
		// Skip the warning modal if there's an active recording, since it continues in the background
		setSelectedPatientId(patient.id);
		setPatientID({
			firstName: patient.firstName,
			lastName: patient.lastName,
			email: patient.email,
			id: patient.id,
			moblieNo: patient.mobile,
		});
		navigate(`${appPages.patientPage.to}/case/${patient.id}`);
	}, [navigate, setPatientID, setSelectedPatientId]);

	const handleEditPatient = useCallback((patient: any) => {
		setedited(true);
		setPatientToEdit(patient);
		setIsModalOpen(true);
	}, []);

	const handleDeletePatient = useCallback((patient: any) => {
		setedited(false);
		setPId(patient.id);
		setIsModalOpen(true);
	}, []);

	return (
		<>
			<AsideBody className={asideBodyClass}>
				<div className='mb-4 w-full'>
					<div className='relative'>
						<input
							type='search'
							placeholder='Search patient name...'
							className='w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all'
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
						<svg
							xmlns='http://www.w3.org/2000/svg'
							className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400'
							fill='currentColor'
							viewBox='0 0 490.4 490.4'>
							<g>
								<path d='M484.1,454.796l-110.5-110.6c29.8-36.3,47.6-82.8,47.6-133.4c0-116.3-94.3-210.6-210.6-210.6S0,94.496,0,210.796   s94.3,210.6,210.6,210.6c50.8,0,97.4-18,133.8-48l110.5,110.5c12.9,11.8,25,4.2,29.2,0C492.5,475.596,492.5,463.096,484.1,454.796z    M41.1,210.796c0-93.6,75.9-169.5,169.5-169.5s169.6,75.9,169.6,169.5s-75.9,169.5-169.5,169.5S41.1,304.396,41.1,210.796z' />
							</g>
						</svg>
					</div>
				</div>

				{/* Conditional Rendering */}
				<div className='w-full'>
					{filteredPatients.length > 0 ? (
						<>
							<div className="mb-2 px-2">
								<div className="text-xs text-gray-500 font-medium uppercase tracking-wider">Patients</div>
								<div className="mt-1 h-px bg-gray-200"></div>
							</div>
							{paginationData.totalPages > paginationData.current && !loading ? (
								<>
									<PatientListCompo
										current={paginationData.current}
										filteredPatients={filteredPatients}
										selectedPatientId={selectedPatientId}
										onSelect={handleredirect}
										onEdit={handleEditPatient}
										onDelete={handleDeletePatient}
									/>
									<Button
										onClick={loadmore}
										className='w-full mt-3 font-medium text-xs'
										size='xs'
										variant='outline'
										color='blue'
										icon='HeroArrowDownOnSquare'>
										Show More
									</Button>
								</>
							) : (
								<>
									<PatientListCompo
										current={paginationData.current}
										filteredPatients={filteredPatients}
										selectedPatientId={selectedPatientId}
										onSelect={handleredirect}
										onEdit={handleEditPatient}
										onDelete={handleDeletePatient}
									/>
								</>
							)}
						</>
					) : !loading && response?.patientList?.length === 0 ?(<div className='m-auto flex h-full flex-col items-center py-8'>
							<div className='bg-blue-50 rounded-lg p-5 mb-4 border border-blue-100'>
								<img
									src={NoPatient as string}
									alt='No patient found'
									className='h-20 w-20 object-contain'
								/>
							</div>
							<div className='mt-2 text-center'>
								<p className='text-lg font-semibold text-gray-800 mb-1'>
									No Patient Found
								</p>
								<p className='text-xs text-gray-600'>
									Try to add a patient to see results.
								</p>
							</div>
						</div>
					):<div className='m-auto flex h-full flex-col items-center justify-center py-8'><SimpleLoader/></div>}
				</div>
			</AsideBody>
			<Modal isOpen={isModalOpen} setIsOpen={setIsModalOpen} isCentered size='sm'>
				{edited ? (
					<></>
				) : (
					<>
						<ModalHeader setIsOpen={setIsModalOpen}>
							{' '}
							{notSavedData ? 'Do you want to continue? ' : ' Confirm Delete'}
						</ModalHeader>
					</>
				)}
				<ModalBody>
					{edited ? (
						<EditPatientPage patientData={patientToEdit} />
					) : (
						<>
							<p className='mb-0 text-lg'>
								{' '}
								{notSavedData
									? 'Unsaved recordings will be lost if you change tabs'
									: 'Are you sure you want to delete this patient?'}
							</p>
						</>
					)}
				</ModalBody>
				<ModalFooter className={`${edited ? 'justify-center' : 'justify-end gap-3 pt-3'}`}>
					{edited ? (
						<Button onClick={handleCloseModal}>Close </Button>
					) : notSavedData ? (
						<>
							<Button
								variant='solid'
								color='gray'
								colorIntensity='500'
								className='cursor-pointer px-4 py-2 text-white'
								onClick={() => {
									setIsModalOpen(false);

									selectedPatientId
										? navigate(
												`${appPages.patientPage.to}/case/${selectedPatientId}`,
											)
										: navigate('/');
								}}>
								Leave
							</Button>
							<Button
								className='cursor-pointer px-4 py-2 text-white'
								color='green'
								variant='solid'
								colorIntensity='600'
								onClick={() => {
									setIsModalOpen(false);
								}}>
								Stay Here
							</Button>
						</>
					) : (
						<>
							<Button
								variant='solid'
								color='gray'
								colorIntensity='500'
								className='cursor-pointer px-4 py-2 text-white'
								onClick={() => setIsModalOpen(false)}>
								Cancel
							</Button>
							<Button
								className='cursor-pointer px-4 py-2 text-white'
								color='red'
								variant='solid'
								colorIntensity='600'
								onClick={() => {
									call_del(PId!).subscribe(() => {
										removeDataSingle(PId!);
										setIsModalOpen(false);
										const toDel = filteredPatients.findIndex(
											(patient: any) => patient.id === PId,
										);
										if (toDel !== -1) {
											filteredPatients.splice(toDel, 1);

											if (filteredPatients.length === 0) {
												setPatientID(null);
											} else {
												setPatientID({
													...filteredPatients[0],
												});
											}
											navigate(`${appPages.dashboardPage.to}`);
										}
									});
								}}>
								Delete
							</Button>
						</>
					)}
				</ModalFooter>
			</Modal>
		</>
	);
};

export default PatientDetails;
